// src/components/common/VideoUpload.js
import React, { useState, useRef } from 'react';
import { Upload, X, CheckCircle, AlertCircle, Video, Loader2 } from 'lucide-react';
import cloudflareStreamService from '../../services/cloudflareStreamService.js';

const VideoUpload = ({ 
  onVideoUpload, 
  onVideoRemove, 
  currentVideo, 
  folder = 'elderfit/videos',
  maxSize = 1024, // MB - Updated to 1GB
  acceptedTypes = ['video/mp4', 'video/webm', 'video/mov', 'video/avi', 'video/mkv'],
  className = '',
  disabled = false
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedTier, setSelectedTier] = useState('basic');
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!acceptedTypes.includes(file.type)) {
      setError(`Please select a valid video file (${acceptedTypes.join(', ')})`);
      return;
    }

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File size must be less than ${maxSize}MB`);
      return;
    }

    setError(null);
    
    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload to Cloudflare Stream
    handleUpload(file);
  };

  const handleUpload = async (file) => {
    setIsUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Simulate progress (Cloudflare doesn't provide upload progress)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const result = await cloudflareStreamService.uploadVideo(
        file, 
        file.name.replace(/\.[^/.]+$/, ""), // Remove file extension
        selectedTier
      );

      clearInterval(progressInterval);
      setUploadProgress(100);

      // Wait a moment to show completion
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
        onVideoUpload(result);
      }, 500);

    } catch (error) {
      setIsUploading(false);
      setUploadProgress(0);
      setError(error.message || 'Upload failed');
    }
  };

  const handleRemove = () => {
    setPreview(null);
    setError(null);
    onVideoRemove && onVideoRemove();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      const event = { target: { files: [file] } };
      handleFileSelect(event);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Tier Selection */}
      <div className="flex items-center space-x-4">
        <label className="text-sm font-medium text-gray-700">Video Tier:</label>
        <select
          value={selectedTier}
          onChange={(e) => setSelectedTier(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          disabled={disabled || isUploading}
        >
          <option value="basic">Basic</option>
          <option value="premium">Premium</option>
          <option value="elite">Elite</option>
        </select>
      </div>

      {/* Upload Area */}
      {!currentVideo && !preview && (
        <div
          className={`border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors ${
            disabled || isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => !disabled && !isUploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes.join(',')}
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled || isUploading}
          />
          
          <Video className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-2">
            {isUploading ? 'Uploading...' : 'Upload Video'}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Drag and drop your video here, or click to browse
          </p>
          <p className="text-xs text-gray-400">
            Supported formats: {acceptedTypes.map(type => type.split('/')[1]).join(', ')} • Max size: {maxSize >= 1024 ? `${maxSize/1024}GB` : `${maxSize}MB`}
          </p>
        </div>
      )}

      {/* Upload Progress */}
      {isUploading && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>Uploading to Cloudflare Stream...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Preview */}
      {preview && !isUploading && (
        <div className="relative">
          <video
            src={preview}
            controls
            className="w-full h-64 object-cover rounded-lg"
          />
          <button
            onClick={handleRemove}
            className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Current Video Display */}
      {currentVideo && !preview && (
        <div className="space-y-2">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <CheckCircle className="w-4 h-4 text-green-500" />
            <span>Video uploaded successfully!</span>
          </div>
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="font-medium">{currentVideo.name}</p>
            <p className="text-sm text-gray-500">ID: {currentVideo.id}</p>
            <p className="text-sm text-gray-500">Tier: {currentVideo.tier}</p>
          </div>
          <button
            onClick={handleRemove}
            className="text-sm text-red-600 hover:text-red-700"
          >
            Remove video
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="flex items-center space-x-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};

export default VideoUpload; 